import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, RotateCcw, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabaseAdmin } from '../config/supabase';
import { storagePublicBaseUrl } from '../config/env';
import { ALL_LEGAL_SETTING_KEYS, LEGAL_DOCUMENTS } from '../constants/legalDocuments';
import BlockEditor from './BlockEditor';
import legalDocumentDefaults from '../data/legalDocumentDefaults.json';
import { blockHasDisplayableContent } from '../utils/facebookVideoEmbed';
import {
  emptyTextBlock,
  normalizeToEditorBlocks,
  sanitizeEditorBlocksForSave,
} from '../utils/legalEditorContent';
import './LegalDocumentsConfig.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function formatUpdatedAt(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('pl-PL');
}

function hasStoredContent(doc, values) {
  const raw = values[doc.settingKey];
  if (raw == null) return false;
  if (typeof raw === 'string') return raw.trim().length > 0;
  return Array.isArray(raw) && raw.length > 0;
}

function getDefaultsForDoc(doc) {
  const raw = legalDocumentDefaults[doc.settingKey];
  if (doc.id === 'privacy') {
    return normalizeToEditorBlocks(null, { html: typeof raw === 'string' ? raw : '' });
  }
  return normalizeToEditorBlocks(null, { legacyBlocks: Array.isArray(raw) ? raw : [] });
}

async function uploadLegalImage(file) {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Dozwolone formaty: JPG, PNG, WEBP.');
  }
  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error('Plik jest za duży (max 5 MB).');
  }
  const ext = (file.name && file.name.split('.').pop()) || 'jpg';
  const fileName = `legal/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`;
  const { error } = await supabaseAdmin.storage
    .from('app-assets')
    .upload(fileName, file, { contentType: file.type || 'image/jpeg', upsert: false });
  if (error) throw error;
  return `${storagePublicBaseUrl}/storage/v1/object/public/app-assets/${fileName}`;
}

export default function LegalDocumentsConfig() {
  const [activeDocId, setActiveDocId] = useState(LEGAL_DOCUMENTS[0].id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});
  const [updatedAt, setUpdatedAt] = useState({});
  const [storedFlags, setStoredFlags] = useState({});
  const [blocks, setBlocks] = useState([emptyTextBlock()]);

  const activeDoc = useMemo(
    () => LEGAL_DOCUMENTS.find((d) => d.id === activeDocId) || LEGAL_DOCUMENTS[0],
    [activeDocId]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseAdmin
        .from('app_settings')
        .select('setting_key, setting_value, updated_at')
        .in('setting_key', ALL_LEGAL_SETTING_KEYS);

      if (error) throw error;

      const nextValues = {};
      const nextUpdatedAt = {};
      const nextStoredFlags = {};
      for (const doc of LEGAL_DOCUMENTS) {
        nextStoredFlags[doc.id] = false;
      }

      (data || []).forEach((row) => {
        nextValues[row.setting_key] = row.setting_value;
        nextUpdatedAt[row.setting_key] = row.updated_at;
        const doc = LEGAL_DOCUMENTS.find((d) => d.settingKey === row.setting_key);
        if (doc) {
          nextStoredFlags[doc.id] = hasStoredContent(doc, { [row.setting_key]: row.setting_value });
        }
      });

      setValues(nextValues);
      setUpdatedAt(nextUpdatedAt);
      setStoredFlags(nextStoredFlags);
    } catch (err) {
      console.error('Error loading legal documents:', err);
      toast.error('Błąd ładowania dokumentów prawnych');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!activeDoc) return;
    const raw = values[activeDoc.settingKey];
    if (hasStoredContent(activeDoc, values)) {
      setBlocks(normalizeToEditorBlocks(raw));
    } else {
      setBlocks(getDefaultsForDoc(activeDoc));
    }
  }, [activeDoc, values]);

  const loadDefaultsForActive = () => {
    setBlocks(getDefaultsForDoc(activeDoc));
    toast.success('Przywrócono domyślną treść z aplikacji mobilnej');
  };

  const handleSave = async () => {
    const sanitized = sanitizeEditorBlocksForSave(blocks);
    if (!sanitized.some(blockHasDisplayableContent)) {
      toast.error('Dodaj treść dokumentu');
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      const { error } = await supabaseAdmin.from('app_settings').upsert(
        {
          setting_key: activeDoc.settingKey,
          setting_value: sanitized,
          updated_at: now,
        },
        { onConflict: 'setting_key' }
      );

      if (error) throw error;

      setValues((prev) => ({ ...prev, [activeDoc.settingKey]: sanitized }));
      setUpdatedAt((prev) => ({ ...prev, [activeDoc.settingKey]: now }));
      setStoredFlags((prev) => ({ ...prev, [activeDoc.id]: true }));
      toast.success(`Zapisano: ${activeDoc.label}`);
    } catch (err) {
      console.error('Error saving legal document:', err);
      toast.error('Błąd zapisu dokumentu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="legal-docs-loading">Ładowanie dokumentów prawnych…</p>;
  }

  const lastSaved = formatUpdatedAt(updatedAt[activeDoc.settingKey]);
  const usesBundledInApp = !storedFlags[activeDoc.id];
  const totalChars = blocks.reduce(
    (sum, block) => sum + (block.type === 'text' ? (block.text || '').length : 0),
    0
  );

  return (
    <section className="legal-docs-config">
      <div className="legal-docs-header">
        <div className="legal-docs-header-icon" aria-hidden>
          <FileText size={22} />
        </div>
        <div>
          <h2 className="legal-docs-title">Dokumenty prawne w aplikacji mobilnej</h2>
          <p className="legal-docs-subtitle">
            Ten sam edytor treści co w aktualnościach i wydarzeniach. Po zapisie zmiany trafią do aplikacji
            mobilnej (odświeżenie lub ponowne wejście na ekran).
          </p>
        </div>
      </div>

      <div className="legal-docs-tabs" role="tablist" aria-label="Dokumenty prawne">
        {LEGAL_DOCUMENTS.map((doc) => (
          <button
            key={doc.id}
            type="button"
            role="tab"
            aria-selected={doc.id === activeDocId}
            className={`legal-docs-tab ${doc.id === activeDocId ? 'active' : ''}`}
            onClick={() => setActiveDocId(doc.id)}
          >
            {doc.label}
            {!storedFlags[doc.id] ? <span className="legal-docs-tab-badge">wbudowana</span> : null}
          </button>
        ))}
      </div>

      <div className="legal-docs-panel">
        <p className="legal-docs-description">{activeDoc.description}</p>
        {usesBundledInApp ? (
          <p className="legal-docs-bundled-note">
            W bazie brak zapisanej treści — poniżej widać wersję wbudowaną z aplikacji. Zapisz, aby publikować
            z panelu.
          </p>
        ) : null}
        {lastSaved ? <p className="legal-docs-meta">Ostatni zapis: {lastSaved}</p> : null}

        <div className="legal-docs-editor-wrap">
          <div className="legal-docs-editor-hints">
            <small>
              Edytor jak w aktualnościach: pogrubienie, wyrównanie, obrazy, linki. Nie wklejaj treści
              bezpośrednio z Word/PDF.
            </small>
            {totalChars > 0 ? <small>Znaki tekstu: {totalChars}</small> : null}
          </div>
          <BlockEditor
            blocks={blocks}
            onBlocksChange={setBlocks}
            onImageUpload={uploadLegalImage}
          />
        </div>

        <div className="legal-docs-actions">
          <button
            type="button"
            className="legal-docs-btn legal-docs-btn-secondary"
            disabled={saving}
            onClick={loadDefaultsForActive}
          >
            <RotateCcw size={18} />
            Przywróć treść z aplikacji
          </button>
          <button
            type="button"
            className="legal-docs-btn legal-docs-btn-primary"
            disabled={saving}
            onClick={handleSave}
          >
            <Save size={18} />
            {saving ? 'Zapisywanie…' : 'Zapisz w aplikacji'}
          </button>
        </div>
      </div>
    </section>
  );
}
